import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2,
  RefreshCw,
  Activity,
  Trophy,
  Zap,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Sparkles,
  MessageCircle,
  Send,
  Users,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { adminSupabase } from '@/lib/admin';
import { fetchPlaceholderSeries, resolvePlayin } from '@/lib/supabase-queries';
import { MemberBetStatusList } from '@/components/MemberBetStatusList';

// ─── Types ────────────────────────────────────────────────────

interface HealthData {
  series: {
    upcoming: number;
    in_progress: number;
    completed: number;
    deprecated: number;
    active: Array<{ id: string; team_home: string; team_away: string; home_wins: number; away_wins: number }>;
    stuck: Array<{ id: string; team_home: string; team_away: string; status: string }>;
  };
  bracket: {
    total: number;
    withPlayIn: number;
    byRound: Record<string, number>;
  };
  sideBets: {
    draft: number;
    open: number;
    locked: number;
    pending_review: number;
    resolved: number;
    stuckOpen: Array<{ id: string; title_he: string; deadline: string }>;
  };
  leaderboard: {
    usersWithPoints: number;
    lastUpdated: string | null;
  };
}

interface GroupItem {
  id: string;
  name: string;
}

interface SeriesItem {
  id: string;
  team_home: string;
  team_away: string;
  status: string;
  home_wins: number | null;
  away_wins: number | null;
  winner: string | null;
  round: string;
}

interface UserLookupResult {
  id: string;
  email: string;
  display_name: string | null;
  groups: Array<{ id: string; name: string; role: string }>;
  bracketExists: boolean;
  betCount: number;
  totalPoints: number;
}

// ─── Helpers ──────────────────────────────────────────────────

const PLAYIN_PLACEHOLDERS = new Set(['W7', 'W8', 'E7', 'E8']);

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
}

function StatusDot({ color }: { color: 'green' | 'yellow' | 'red' }) {
  const bg = color === 'green' ? 'bg-green-500' : color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${bg}`} />;
}

function StatCard({ label, value, color }: { label: string; value: number | string; color?: 'green' | 'yellow' | 'red' }) {
  return (
    <div className="bg-background/50 rounded-xl border border-border p-3 text-center">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5 mt-1">
        {color && <StatusDot color={color} />}
        {label}
      </p>
    </div>
  );
}

// ─── Play-In Resolution Section ───────────────────────────────

interface PlayinInputs {
  team_home: string;
  team_away: string;
  team_home_logo: string;
  team_away_logo: string;
}

function PlayInSection() {
  const { toast } = useToast();
  const [placeholderSeries, setPlaceholderSeries] = useState<Array<{ id: string; team_home: string; team_away: string; conference: string }>>([]);
  const [playinInputs, setPlayinInputs] = useState<Record<string, PlayinInputs>>({});
  const [resolvingPlayin, setResolvingPlayin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPlaceholders = useCallback(async () => {
    setLoading(true);
    try {
      const placeholders = await fetchPlaceholderSeries().catch(() => []);
      setPlaceholderSeries(placeholders);
      const inputs: Record<string, PlayinInputs> = {};
      for (const s of placeholders) {
        inputs[s.id] = { team_home: '', team_away: '', team_home_logo: '', team_away_logo: '' };
      }
      setPlayinInputs(inputs);
    } catch (err) {
      console.error('[Admin] Play-In fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlaceholders(); }, [loadPlaceholders]);

  const resolutionCount = Object.values(playinInputs).filter(v => v.team_home.trim() && v.team_away.trim()).length;

  const handleResolvePlayin = async () => {
    const resolutions = Object.entries(playinInputs)
      .filter(([, v]) => v.team_home.trim() && v.team_away.trim())
      .map(([series_id, v]) => ({
        series_id,
        team_home: v.team_home.trim(),
        team_away: v.team_away.trim(),
        team_home_logo: v.team_home_logo.trim() || null,
        team_away_logo: v.team_away_logo.trim() || null,
      }));

    if (resolutions.length === 0) {
      toast({ title: 'יש להזין שמות קבוצות לפחות לסדרה אחת', variant: 'destructive' });
      return;
    }

    setResolvingPlayin(true);
    try {
      console.log('[Admin] Resolving play-in:', resolutions);
      const result = await resolvePlayin(resolutions);
      toast({
        title: 'תוצאות הפליי-אין עודכנו בהצלחה',
        description: `${result.resolved} סדרות עודכנו, ${result.entries_updated} ברקטים עודכנו`,
      });
      await loadPlaceholders();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[Admin] Play-in resolve error:', err);
      toast({ title: 'שגיאה בעדכון הפליי-אין', description: msg, variant: 'destructive' });
    }
    setResolvingPlayin(false);
    setConfirmOpen(false);
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="w-5 h-5" /> עדכון Play-In</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="w-5 h-5" /> עדכון Play-In</CardTitle>
      </CardHeader>
      <CardContent>
        {placeholderSeries.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            כל סדרות הפליי-אין עודכנו — אין Placeholders
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {placeholderSeries.length} סדרות ממתינות לעדכון. הזן שמות קבוצות — פעולה זו תעדכן את כל הברקטים הקיימים.
            </p>

            {placeholderSeries.map((s) => {
              const conf = s.conference === 'west' ? 'מערב' : s.conference === 'east' ? 'מזרח' : s.conference;
              const inputs = playinInputs[s.id] || { team_home: '', team_away: '', team_home_logo: '', team_away_logo: '' };
              return (
                <div key={s.id} className="bg-background/30 rounded-xl border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{s.team_home} נגד {s.team_away}</span>
                    <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-accent/80">{conf}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">קבוצת בית</label>
                      <Input
                        value={inputs.team_home}
                        onChange={(e) => setPlayinInputs(prev => ({ ...prev, [s.id]: { ...prev[s.id], team_home: e.target.value } }))}
                        placeholder="שם הקבוצה"
                        dir="rtl"
                        className="bg-background text-sm h-8"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">קבוצת חוץ</label>
                      <Input
                        value={inputs.team_away}
                        onChange={(e) => setPlayinInputs(prev => ({ ...prev, [s.id]: { ...prev[s.id], team_away: e.target.value } }))}
                        placeholder="שם הקבוצה"
                        dir="rtl"
                        className="bg-background text-sm h-8"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">לוגו בית (URL, אופציונלי)</label>
                      <Input
                        value={inputs.team_home_logo}
                        onChange={(e) => setPlayinInputs(prev => ({ ...prev, [s.id]: { ...prev[s.id], team_home_logo: e.target.value } }))}
                        placeholder="https://..."
                        dir="ltr"
                        className="bg-background text-sm h-8"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">לוגו חוץ (URL, אופציונלי)</label>
                      <Input
                        value={inputs.team_away_logo}
                        onChange={(e) => setPlayinInputs(prev => ({ ...prev, [s.id]: { ...prev[s.id], team_away_logo: e.target.value } }))}
                        placeholder="https://..."
                        dir="ltr"
                        className="bg-background text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end">
              <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogTrigger asChild>
                  <Button size="sm" className="gap-1.5" disabled={resolvingPlayin || resolutionCount === 0}>
                    {resolvingPlayin ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    עדכן ({resolutionCount})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>עדכון תוצאות Play-In</AlertDialogTitle>
                    <AlertDialogDescription>
                      פעולה זו תעדכן {resolutionCount} סדרות ותשכתב את כל הברקטים הקיימים.
                      <br />
                      לא ניתן לבטל פעולה זו.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>ביטול</AlertDialogCancel>
                    <AlertDialogAction onClick={handleResolvePlayin} disabled={resolvingPlayin}>
                      {resolvingPlayin ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
                      אישור — עדכן ברקטים
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Data Health Section ──────────────────────────────────────

function DataHealthSection() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      // Parallel fetches
      const [seriesRes, bracketRes, sideBetsRes, leaderboardRes] = await Promise.all([
        supabase.from('series').select('id, team_home, team_away, status, home_wins, away_wins, winner, round, updated_at'),
        supabase.from('bracket_entries').select('id, picks'),
        supabase.from('global_side_bets').select('id, title_he, status, deadline'),
        supabase.from('leaderboard').select('total_points, updated_at'),
      ]);

      // Series
      const allSeries = seriesRes.data ?? [];
      const statusCounts = { upcoming: 0, in_progress: 0, completed: 0, deprecated: 0 };
      const active: HealthData['series']['active'] = [];
      const stuck: HealthData['series']['stuck'] = [];
      for (const s of allSeries) {
        const st = s.status as keyof typeof statusCounts;
        if (st in statusCounts) statusCounts[st]++;
        if (s.status === 'in_progress') {
          active.push({ id: s.id, team_home: s.team_home, team_away: s.team_away, home_wins: s.home_wins ?? 0, away_wins: s.away_wins ?? 0 });
        }
        // Stuck = unexpected status not in known set
        if (s.status && !['upcoming', 'in_progress', 'completed', 'deprecated'].includes(s.status)) {
          stuck.push({ id: s.id, team_home: s.team_home, team_away: s.team_away, status: s.status });
        }
      }

      // Brackets
      const allBrackets = bracketRes.data ?? [];
      let withPlayIn = 0;
      const roundCounts: Record<string, number> = {};
      for (const entry of allBrackets) {
        const picks = (entry.picks ?? {}) as Record<string, { winner?: string }>;
        const pickValues = Object.values(picks);
        // Check for play-in placeholders
        if (pickValues.some(p => p.winner && PLAYIN_PLACEHOLDERS.has(p.winner))) {
          withPlayIn++;
        }
        // Count how many picks per entry to estimate round fill
        const count = pickValues.length;
        if (count >= 15) roundCounts['Finals'] = (roundCounts['Finals'] ?? 0) + 1;
        if (count >= 14) roundCounts['CF'] = (roundCounts['CF'] ?? 0) + 1;
        if (count >= 12) roundCounts['R2'] = (roundCounts['R2'] ?? 0) + 1;
        if (count >= 8) roundCounts['R1'] = (roundCounts['R1'] ?? 0) + 1;
      }

      // Side bets
      const allSideBets = sideBetsRes.data ?? [];
      const sbCounts = { draft: 0, open: 0, locked: 0, pending_review: 0, resolved: 0 };
      const stuckOpen: HealthData['sideBets']['stuckOpen'] = [];
      const now = new Date();
      for (const sb of allSideBets) {
        const st = sb.status as keyof typeof sbCounts;
        if (st in sbCounts) sbCounts[st]++;
        if (sb.status === 'open' && sb.deadline && new Date(sb.deadline) < now) {
          stuckOpen.push({ id: sb.id, title_he: sb.title_he, deadline: sb.deadline });
        }
      }

      // Leaderboard
      const allLeaderboard = leaderboardRes.data ?? [];
      const usersWithPoints = allLeaderboard.filter(l => (l.total_points ?? 0) > 0).length;
      const lastUpdated = allLeaderboard
        .map(l => l.updated_at)
        .filter(Boolean)
        .sort()
        .pop() ?? null;

      setData({
        series: { ...statusCounts, active, stuck },
        bracket: { total: allBrackets.length, withPlayIn, byRound: roundCounts },
        sideBets: { ...sbCounts, stuckOpen },
        leaderboard: { usersWithPoints, lastUpdated },
      });
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[Admin] Health fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading && !data) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Activity className="w-5 h-5" /> בריאות המערכת</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const hasIssues = data.series.stuck.length > 0 || data.sideBets.stuckOpen.length > 0 || data.bracket.withPlayIn > 0;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5" />
          בריאות המערכת
          {hasIssues && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
        </CardTitle>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          {formatTime(lastRefresh.toISOString())}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchHealth} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Series */}
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-[0.15em]">סדרות</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatCard label="ממתינות" value={data.series.upcoming} color="yellow" />
            <StatCard label="פעילות" value={data.series.in_progress} color="green" />
            <StatCard label="הושלמו" value={data.series.completed} color="green" />
            <StatCard label="מבוטלות" value={data.series.deprecated} color={data.series.deprecated > 0 ? 'yellow' : 'green'} />
          </div>
          {data.series.active.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs text-muted-foreground">סדרות פעילות:</p>
              {data.series.active.map(s => (
                <div key={s.id} className="text-xs bg-background/30 rounded px-2 py-1 flex justify-between">
                  <span>{s.team_home} vs {s.team_away}</span>
                  <span className="font-mono">{s.home_wins}-{s.away_wins}</span>
                </div>
              ))}
            </div>
          )}
          {data.series.stuck.length > 0 && (
            <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/30">
              <p className="text-xs text-red-400 font-semibold">סטטוס לא צפוי:</p>
              {data.series.stuck.map(s => (
                <p key={s.id} className="text-xs text-red-300">{s.team_home} vs {s.team_away} — {s.status}</p>
              ))}
            </div>
          )}
        </div>

        {/* Brackets */}
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-[0.15em]">ברקטים</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <StatCard label="סה״כ" value={data.bracket.total} />
            <StatCard label="עם Play-In" value={data.bracket.withPlayIn} color={data.bracket.withPlayIn > 0 ? 'yellow' : 'green'} />
            {Object.entries(data.bracket.byRound).map(([round, count]) => (
              <StatCard key={round} label={`מילאו ${round}`} value={count} />
            ))}
          </div>
        </div>

        {/* Side Bets */}
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-[0.15em]">הימורי צד גלובליים</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <StatCard label="טיוטה" value={data.sideBets.draft} color={data.sideBets.draft > 0 ? 'yellow' : 'green'} />
            <StatCard label="פתוחים" value={data.sideBets.open} color="green" />
            <StatCard label="נעולים" value={data.sideBets.locked} color="green" />
            <StatCard label="ממתינים לאישור" value={data.sideBets.pending_review} color={data.sideBets.pending_review > 0 ? 'yellow' : 'green'} />
            <StatCard label="נפתרו" value={data.sideBets.resolved} color="green" />
          </div>
          {data.sideBets.stuckOpen.length > 0 && (
            <div className="mt-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-xs text-yellow-400 font-semibold">פתוחים אחרי הדדליין:</p>
              {data.sideBets.stuckOpen.map(sb => (
                <p key={sb.id} className="text-xs text-yellow-300">{sb.title_he} — דדליין: {formatTime(sb.deadline)}</p>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-[0.15em]">לידרבורד</h3>
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="משתמשים עם ניקוד" value={data.leaderboard.usersWithPoints} color="green" />
            <StatCard label="עדכון אחרון" value={formatTime(data.leaderboard.lastUpdated)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Manual Tools Section ─────────────────────────────────────

function ManualToolsSection() {
  const { toast } = useToast();

  // State for tools
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [seriesList, setSeriesList] = useState<SeriesItem[]>([]);
  const [loadingInit, setLoadingInit] = useState(true);

  // Recalculate leaderboard
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcAllLoading, setRecalcAllLoading] = useState(false);

  // Force series status
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [forceWinner, setForceWinner] = useState('');
  const [forceFinalScore, setForceFinalScore] = useState('');
  const [forcingStatus, setForcingStatus] = useState(false);

  // Edge function triggers
  const [triggerLoading, setTriggerLoading] = useState<Record<string, boolean>>({});
  const [triggerResults, setTriggerResults] = useState<Record<string, { ok: boolean; message: string }>>({});

  // User lookup
  const [userSearch, setUserSearch] = useState('');
  const [userResult, setUserResult] = useState<UserLookupResult | null>(null);
  const [userSearching, setUserSearching] = useState(false);

  // Load groups and series
  useEffect(() => {
    (async () => {
      try {
        const [groupsRes, seriesRes] = await Promise.all([
          // Service-role client so the super-admin view lists ALL groups; the
          // authenticated client only sees the admin's own groups after the M-1
          // policy fix. Falls back to authenticated if the key isn't configured.
          (adminSupabase ?? supabase).from('groups').select('id, name').order('name'),
          supabase.from('series').select('id, team_home, team_away, status, home_wins, away_wins, winner, round').order('round'),
        ]);
        setGroups(groupsRes.data ?? []);
        setSeriesList(seriesRes.data ?? []);
      } catch (err) {
        console.error('[Admin] Init fetch error:', err);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, []);

  // ── Tool 1: Recalculate Leaderboard ──

  const handleRecalculate = async (groupId: string) => {
    setRecalculating(true);
    try {
      console.log('[Admin] Recalculating leaderboard for group:', groupId);
      // Service-role: runs the recompute regardless of commissioner membership
      // (the recalculate_leaderboard guard bypasses internal/service-role callers).
      const { error } = await (adminSupabase ?? supabase).rpc('recalculate_leaderboard', { target_group_id: groupId });
      if (error) throw error;
      const groupName = groups.find(g => g.id === groupId)?.name ?? groupId;
      toast({ title: 'לידרבורד חושב מחדש', description: groupName });
    } catch (err: any) {
      console.error('[Admin] Recalculate error:', err);
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    } finally {
      setRecalculating(false);
    }
  };

  const handleRecalcAll = async () => {
    setRecalcAllLoading(true);
    let success = 0;
    let failed = 0;
    try {
      for (const group of groups) {
        console.log('[Admin] Recalculating all — group:', group.name);
        const { error } = await (adminSupabase ?? supabase).rpc('recalculate_leaderboard', { target_group_id: group.id });
        if (error) { failed++; console.error('[Admin] Recalc failed for', group.name, error); }
        else success++;
      }
      toast({ title: 'חישוב מחדש הושלם', description: `${success} הצליחו, ${failed} נכשלו` });
    } catch (err: any) {
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    } finally {
      setRecalcAllLoading(false);
    }
  };

  // ── Tool 2: Force Series Status ──

  const selectedSeriesData = seriesList.find(s => s.id === selectedSeries);

  const handleForceStatus = async () => {
    if (!selectedSeries || !newStatus) return;
    setForcingStatus(true);
    try {
      const update: Record<string, any> = { status: newStatus };
      if (newStatus === 'completed') {
        if (forceWinner) update.winner = forceWinner;
        if (forceFinalScore) update.final_score = forceFinalScore;
      }
      console.log('[Admin] Force series status:', selectedSeries, update);
      const { error } = await supabase.from('series').update(update).eq('id', selectedSeries);
      if (error) throw error;
      toast({ title: 'סטטוס עודכן', description: `${selectedSeriesData?.team_home} vs ${selectedSeriesData?.team_away} → ${newStatus}` });
      // Refresh series list
      const { data } = await supabase.from('series').select('id, team_home, team_away, status, home_wins, away_wins, winner, round').order('round');
      if (data) setSeriesList(data);
      setNewStatus('');
      setForceWinner('');
      setForceFinalScore('');
    } catch (err: any) {
      console.error('[Admin] Force status error:', err);
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    } finally {
      setForcingStatus(false);
    }
  };

  // ── Tool 3: Edge Function Triggers ──

  const edgeFunctions = [
    { key: 'update-series-scores', label: 'עדכן תוצאות מ-ESPN', icon: Activity },
    { key: 'resolve-bets', label: 'פתור הימורים', icon: CheckCircle2 },
    { key: 'advance-round', label: 'קדם סיבוב', icon: Zap },
    { key: 'scrape-draftkings', label: 'עדכן אודס מ-DraftKings', icon: RefreshCw },
    { key: 'populate-side-bets', label: 'צור הימורי צד (טיוטה)', icon: Sparkles },
    { key: 'resolve-side-bets', label: 'פתור הימורי צד', icon: Trophy },
    { key: 'resolve-playin', label: 'פתור Play-In', icon: Sparkles },
  ];

  const triggerEdgeFunction = async (fnName: string) => {
    setTriggerLoading(prev => ({ ...prev, [fnName]: true }));
    setTriggerResults(prev => { const n = { ...prev }; delete n[fnName]; return n; });
    try {
      if (!adminSupabase) {
        throw new Error('VITE_SUPABASE_SERVICE_ROLE_KEY not configured');
      }
      console.log('[Admin] Triggering edge function:', fnName);
      const { data, error } = await adminSupabase.functions.invoke(fnName);
      if (error) throw error;
      console.log('[Admin] Edge function result:', fnName, data);
      setTriggerResults(prev => ({ ...prev, [fnName]: { ok: true, message: JSON.stringify(data, null, 2)?.slice(0, 200) ?? 'OK' } }));
      toast({ title: `${fnName} הושלם` });
    } catch (err: any) {
      console.error('[Admin] Edge function error:', fnName, err);
      setTriggerResults(prev => ({ ...prev, [fnName]: { ok: false, message: err.message } }));
      toast({ title: `שגיאה ב-${fnName}`, description: err.message, variant: 'destructive' });
    } finally {
      setTriggerLoading(prev => ({ ...prev, [fnName]: false }));
    }
  };

  // ── Tool 4: User Lookup ──

  const handleUserSearch = async () => {
    if (!userSearch.trim()) return;
    setUserSearching(true);
    setUserResult(null);
    try {
      const query = userSearch.trim();
      console.log('[Admin] User lookup:', query);

      // Search in leaderboard for display_name match (since we can't query auth.users directly from client)
      const { data: lbRows } = await supabase
        .from('leaderboard')
        .select('user_id, display_name, total_points')
        .ilike('display_name', `%${query}%`)
        .limit(1);

      // Also try bracket_entries
      const { data: beRows } = await supabase
        .from('bracket_entries')
        .select('user_id, display_name')
        .ilike('display_name', `%${query}%`)
        .limit(1);

      const userId = lbRows?.[0]?.user_id ?? beRows?.[0]?.user_id;
      const displayName = lbRows?.[0]?.display_name ?? beRows?.[0]?.display_name;

      if (!userId) {
        toast({ title: 'לא נמצא משתמש', variant: 'destructive' });
        setUserSearching(false);
        return;
      }

      // Fetch user's groups
      const { data: memberships } = await supabase
        .from('group_members')
        .select('group_id, role')
        .eq('user_id', userId);

      const groupIds = (memberships ?? []).map(m => m.group_id).filter(Boolean);
      let userGroups: Array<{ id: string; name: string; role: string }> = [];
      if (groupIds.length > 0) {
        const { data: gData } = await supabase.from('groups').select('id, name').in('id', groupIds);
        userGroups = (gData ?? []).map(g => ({
          id: g.id,
          name: g.name,
          role: memberships?.find(m => m.group_id === g.id)?.role ?? 'member',
        }));
      }

      // Bracket
      const { data: bracket } = await supabase.from('bracket_entries').select('id').eq('user_id', userId).maybeSingle();

      // Bets count
      const { count: betCount } = await supabase.from('bets').select('id', { count: 'exact', head: true }).eq('user_id', userId);

      // Total points across groups
      const { data: allLb } = await supabase.from('leaderboard').select('total_points').eq('user_id', userId);
      const totalPoints = (allLb ?? []).reduce((sum, r) => sum + (r.total_points ?? 0), 0);

      setUserResult({
        id: userId,
        email: '(not available from client)',
        display_name: displayName,
        groups: userGroups,
        bracketExists: !!bracket,
        betCount: betCount ?? 0,
        totalPoints,
      });
    } catch (err: any) {
      console.error('[Admin] User lookup error:', err);
      toast({ title: 'שגיאה בחיפוש', description: err.message, variant: 'destructive' });
    } finally {
      setUserSearching(false);
    }
  };

  if (loadingInit) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5" /> כלים ידניים</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5" /> כלים ידניים</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Tool 1: Recalculate Leaderboard */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-[0.15em]">חשב לידרבורד מחדש</h3>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="בחר קבוצה" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={!selectedGroup || recalculating}>
                  {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'חשב מחדש'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle>חישוב מחדש</AlertDialogTitle>
                  <AlertDialogDescription>
                    לחשב מחדש את הלידרבורד של {groups.find(g => g.id === selectedGroup)?.name ?? 'הקבוצה'}?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>ביטול</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleRecalculate(selectedGroup)}>אישור</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={recalcAllLoading}>
                  {recalcAllLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'חשב הכל'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle>חישוב כל הקבוצות</AlertDialogTitle>
                  <AlertDialogDescription>
                    לחשב מחדש את הלידרבורד של כל {groups.length} הקבוצות?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>ביטול</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRecalcAll}>אישור</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Tool 2: Force Series Status */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-[0.15em]">שנה סטטוס סדרה</h3>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Select value={selectedSeries} onValueChange={(v) => { setSelectedSeries(v); setNewStatus(''); }}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="בחר סדרה" />
                </SelectTrigger>
                <SelectContent>
                  {seriesList.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.team_home} vs {s.team_away} ({s.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="סטטוס חדש" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">upcoming</SelectItem>
                  <SelectItem value="in_progress">in_progress</SelectItem>
                  <SelectItem value="completed">completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {newStatus === 'completed' && (
            <div className="flex gap-2 flex-wrap">
              <Input
                placeholder="מנצח (שם הקבוצה)"
                value={forceWinner}
                onChange={e => setForceWinner(e.target.value)}
                className="bg-background flex-1 min-w-[140px]"
              />
              <Input
                placeholder="תוצאה סופית (4-2)"
                value={forceFinalScore}
                onChange={e => setForceFinalScore(e.target.value)}
                className="bg-background w-[140px]"
              />
            </div>
          )}
          {selectedSeries && newStatus && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={forcingStatus}>
                  {forcingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'עדכן סטטוס'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle>עדכון סטטוס סדרה</AlertDialogTitle>
                  <AlertDialogDescription>
                    {selectedSeriesData?.team_home} vs {selectedSeriesData?.team_away}
                    <br />
                    {selectedSeriesData?.status} → {newStatus}
                    {newStatus === 'completed' && forceWinner && <><br />מנצח: {forceWinner}</>}
                    {newStatus === 'completed' && forceFinalScore && <><br />תוצאה: {forceFinalScore}</>}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>ביטול</AlertDialogCancel>
                  <AlertDialogAction onClick={handleForceStatus}>אישור</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Tool 3: Edge Function Triggers */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-[0.15em]">הפעל Edge Functions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {edgeFunctions.map(fn => (
              <div key={fn.key} className="space-y-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2"
                      disabled={triggerLoading[fn.key]}
                    >
                      {triggerLoading[fn.key] ? <Loader2 className="w-4 h-4 animate-spin" /> : <fn.icon className="w-4 h-4" />}
                      {fn.label}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>הפעלת {fn.key}</AlertDialogTitle>
                      <AlertDialogDescription>להפעיל את {fn.label}?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>ביטול</AlertDialogCancel>
                      <AlertDialogAction onClick={() => triggerEdgeFunction(fn.key)}>הפעל</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {triggerResults[fn.key] && (
                  <p className={`text-xs px-2 py-1 rounded ${triggerResults[fn.key].ok ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {triggerResults[fn.key].message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tool 4: User Lookup */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-[0.15em]">חיפוש משתמש</h3>
          <div className="flex gap-2">
            <Input
              placeholder="שם תצוגה..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleUserSearch()}
              className="bg-background flex-1"
            />
            <Button variant="outline" size="sm" onClick={handleUserSearch} disabled={userSearching || !userSearch.trim()}>
              {userSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {userResult && (
            <div className="bg-background/30 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">שם:</span>
                <span className="font-medium">{userResult.display_name ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID:</span>
                <span className="font-mono text-xs">{userResult.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ברקט:</span>
                <span>{userResult.bracketExists ? <CheckCircle2 className="w-4 h-4 text-green-500 inline" /> : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">הימורים:</span>
                <span>{userResult.betCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ניקוד כולל:</span>
                <span className="font-bold">{userResult.totalPoints}</span>
              </div>
              {userResult.groups.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs mb-1">קבוצות:</p>
                  {userResult.groups.map(g => (
                    <p key={g.id} className="text-xs">{g.name} ({g.role})</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
}

// ─── Member Status Section ────────────────────────────────────

function MemberStatusSection() {
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [seriesList, setSeriesList] = useState<SeriesItem[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [groupsRes, seriesRes] = await Promise.all([
          // Service-role client so the super-admin view lists ALL groups; the
          // authenticated client only sees the admin's own groups after the M-1
          // policy fix. Falls back to authenticated if the key isn't configured.
          (adminSupabase ?? supabase).from('groups').select('id, name').order('name'),
          supabase.from('series').select('id, team_home, team_away, status, home_wins, away_wins, winner, round').order('round'),
        ]);
        setGroups(groupsRes.data ?? []);
        setSeriesList(seriesRes.data ?? []);
      } catch (err) {
        console.error('[Admin] MemberStatus init error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5" />
          סטטוס חברי קבוצה
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <>
            <div className="max-w-sm">
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="בחר קבוצה" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedGroup ? (
              <MemberBetStatusList
                key={selectedGroup}
                groupId={selectedGroup}
                seriesList={seriesList}
              />
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                בחר קבוצה כדי לראות סטטוס הימורים של כל חבר
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Telegram Monitor Section ─────────────────────────────────

interface NotificationLogEntry {
  id: string;
  channel: string;
  message_type: string;
  status: string | null;
  sent_at: string | null;
  group_id: string | null;
}

function TelegramMonitorSection() {
  const { toast } = useToast();
  const [botStatus, setBotStatus] = useState<{ ok: boolean; username?: string } | null>(null);
  const [checkingBot, setCheckingBot] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [recentLogs, setRecentLogs] = useState<NotificationLogEntry[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // Load recent notifications
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from('notifications_log')
          .select('id, channel, message_type, status, sent_at, group_id')
          .order('sent_at', { ascending: false })
          .limit(10);
        setRecentLogs(data ?? []);
      } catch (err) {
        console.error('[Admin] Notification log fetch error:', err);
      } finally {
        setLoadingLogs(false);
      }
    })();
  }, []);

  // Test bot connectivity via Edge Function
  const handleTestBot = async () => {
    setCheckingBot(true);
    try {
      if (!adminSupabase) throw new Error('VITE_SUPABASE_SERVICE_ROLE_KEY not configured');
      const { data, error } = await adminSupabase.functions.invoke('telegram-notify', {
        body: { event_type: 'health_check' },
      });
      if (error) throw error;
      // The function may return an error for unknown event_type, which is fine —
      // if it responded at all, the function is reachable
      setBotStatus({ ok: true, username: data?.bot_username ?? 'connected' });
      toast({ title: 'Telegram Bot פעיל' });
    } catch (err: any) {
      console.error('[Admin] Bot check error:', err);
      setBotStatus({ ok: false });
      toast({ title: 'Telegram Bot לא זמין', description: err.message, variant: 'destructive' });
    } finally {
      setCheckingBot(false);
    }
  };

  // Send test message
  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      if (!adminSupabase) throw new Error('VITE_SUPABASE_SERVICE_ROLE_KEY not configured');
      const ts = new Date().toLocaleString('he-IL');
      const { error } = await adminSupabase.functions.invoke('telegram-notify', {
        body: { event_type: 'test_admin_message' },
      });
      if (error) throw error;
      toast({ title: 'הודעת בדיקה נשלחה', description: ts });
    } catch (err: any) {
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          מוניטור Telegram
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bot Status & Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleTestBot} disabled={checkingBot} className="gap-1.5">
            {checkingBot ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
            בדוק חיבור
          </Button>
          <Button variant="outline" size="sm" onClick={handleSendTest} disabled={sendingTest} className="gap-1.5">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            שלח הודעת בדיקה
          </Button>
          {botStatus && (
            <span className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${botStatus.ok ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              <StatusDot color={botStatus.ok ? 'green' : 'red'} />
              {botStatus.ok ? `Bot פעיל (${botStatus.username})` : 'Bot לא זמין'}
            </span>
          )}
        </div>

        {/* Recent Notifications */}
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-[0.15em]">10 התראות אחרונות</h3>
          {loadingLogs ? (
            <Skeleton className="h-32 w-full" />
          ) : recentLogs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">אין התראות</p>
          ) : (
            <div className="space-y-1">
              {recentLogs.map(log => (
                <div key={log.id} className="flex items-center justify-between text-xs bg-background/30 rounded px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <StatusDot color={log.status === 'sent' ? 'green' : log.status === 'failed' ? 'red' : 'yellow'} />
                    <span className="font-medium">{log.message_type}</span>
                    <span className="text-muted-foreground">{log.channel}</span>
                  </div>
                  <span className="text-muted-foreground">{formatTime(log.sent_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────

const AdminPage = () => {
  const navigate = useNavigate();
  const admin = useAdmin();

  useEffect(() => {
    if (!admin) navigate('/', { replace: true });
  }, [admin, navigate]);

  if (!admin) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl font-bold text-destructive">אין גישה</p>
          <p className="text-muted-foreground mt-2 text-sm">עמוד זה מוגבל למנהלים בלבד</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="container mx-auto px-4 py-8 space-y-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl font-bold tracking-tight">לוח בקרה — מנהל</h1>

        <div className="space-y-6">
          <PlayInSection />

          <DataHealthSection />
          <ManualToolsSection />

          <MemberStatusSection />

          <TelegramMonitorSection />
        </div>
      </motion.div>
    </Layout>
  );
};

export default AdminPage;
